import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
} from 'antd';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import moment from 'moment-timezone';
import { usePermify } from '@permify/react-role';
import { isPercentageModel } from '../../../core/utils/pricing/cpmHelper';
import { useTimezone } from '../../../core/hooks/useTimezone';
import {
  toDatePickerDisplay,
  datePickerToStartOfDayUtc,
  datePickerToEndOfDayUtc,
  getDefaultStartDate,
  getDefaultEndDate,
} from './utils/dateNormalization';

const { Text } = Typography;

// Wrapper that converts Form.Item's injected UTC value to user timezone for display
const TimezoneDatePicker = ({ value, onChange, timezone, ...props }) => {
  const displayValue = toDatePickerDisplay(value, timezone);
  return <DatePicker {...props} value={displayValue} onChange={onChange} />;
};

const CampaignRequestorBudget = ({ formValues, derivedState, helpers }) => {
  const { currentUserOrg, currentCampaignOrder, selectedAdvertiser } =
    useCampaignRequestorContext();
  const { isAuthorized } = usePermify();
  const { timezone } = useTimezone();
  const [canEdit, setCanEdit] = useState(false);
  const [budgetInputValue, setBudgetInputValue] = useState(null);

  // Extract reactive values
  const { advertiserId, startDate, endDate, budget, cpm } = formValues;

  // Check if pricing model is Percentage
  // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
  const savedPricingModel = currentCampaignOrder?.pricingModel;
  const hideCpmFields = useMemo(() => {
    if (savedPricingModel) {
      return savedPricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [savedPricingModel, selectedAdvertiser]);

  const handleStartDateChange = (value) => {
    let updatedEndDate = endDate;
    // Normalize start date to start of day in user timezone, then convert to UTC for storage
    let updatedStartDate = value ? datePickerToStartOfDayUtc(value, timezone) : value;

    if (value) {
      if (value.isAfter(endDate)) {
        updatedEndDate = null;
      }
    }

    // Update start date using helper
    helpers.updateField('startDate', updatedStartDate);

    // Check if end date is set and is greater than 2 days after start date
    if (endDate && moment(endDate).isAfter(moment(value.format('YYYY-MM-DD')).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
      return;
    }

    // Set end date 2 days after start date, normalized to end of day in user timezone, then UTC
    if (value) {
      updatedEndDate = moment.tz(value.format('YYYY-MM-DD'), timezone).add(2, 'days').endOf('day').utc();
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
    }
  };

  const handleEndDateChange = (value) => {
    // Normalize end date to end of day in user timezone, then convert to UTC for storage
    let updatedEndDate = value ? datePickerToEndOfDayUtc(value, timezone) : value;
    let updatedStartDate = startDate;

    if (value) {
      if (value.isBefore(startDate)) {
        updatedStartDate = null;
      }
    }

    // Update end date using helper
    helpers.updateField('endDate', updatedEndDate);

    // Check if start date is set and end date is greater than 2 days after start date
    if (startDate && moment(value.format('YYYY-MM-DD')).isAfter(moment(startDate.format('YYYY-MM-DD')).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
      return;
    }

    // Set start date 2 days before end date, normalized to start of day in user timezone, then UTC
    if (value) {
      updatedStartDate = moment.tz(value.format('YYYY-MM-DD'), timezone).subtract(2, 'days').startOf('day').utc();
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
    }
  };

  const disabledEndDate = (date) => {
    if (!startDate) {
      return true;
    }

    const startInTz = toDatePickerDisplay(startDate, timezone);
    if (date.isBefore(startInTz)) {
      return true;
    }

    const start = startInTz.clone().startOf('day');
    const end = startInTz.clone().add(2, 'days').endOf('day');

    return date.isBetween(start, end, null, '[]');
  };

  const handleCpmBlur = (e) => {
    const value = Number(e.target.value);
    // Use the helper's CPM correction logic
    helpers.handleCpmBlur(value);
  };

  const handleCpmChange = () => {
    // Let Form.Item handle the value update automatically
  };

  // Handle budget input change (captures the value but doesn't update form immediately)
  const handleBudgetChange = (value) => {
    setBudgetInputValue(value);
  };

  // Debounced budget update - only updates form value after user stops typing for 500ms
  useEffect(() => {
    if (budgetInputValue === null) {
      return;
    }

    // Debounce the update by 500ms
    const timeoutId = setTimeout(() => {
      helpers.updateField('budget', budgetInputValue);
    }, 500);

    // Cleanup: cancel the timeout if budgetInputValue changes again before 500ms
    return () => clearTimeout(timeoutId);
  }, [budgetInputValue, helpers]);

  useEffect(() => {
    const checkPermission = async () => {
      const authorized = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setCanEdit(authorized);
    };

    checkPermission();
  }, [isAuthorized]);

  return (
    <>
      <Card id="requester-duration-budget" title="ORDER BUDGET & SCHEDULE">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {(() => {
            // "Today" must use the user's LOCAL browser calendar day — NOT moment.tz(timezone).
            // When the org timezone is UTC, moment.tz('UTC') can be a calendar day AHEAD of
            // the user's local day (e.g. still 06/15 locally but already 06/16 in UTC), which
            // wrongly flags today's start date as "in the past". This mirrors the Launch
            // Readiness check (validateAllDatesForLaunch) so the two stay consistent.
            const todayStr = moment().format('YYYY-MM-DD');
            const campaignStartStr = startDate
              ? (moment.isMoment(startDate)
                  ? startDate.utc().format('YYYY-MM-DD')
                  : moment.utc(startDate).format('YYYY-MM-DD'))
              : null;
            const campaignStatus = currentCampaignOrder?.status;

            if (startDate && campaignStartStr < todayStr) {
              // Hide notice if campaign is already live or live_pending
              if (
                campaignStatus === 'LIVE' ||
                campaignStatus === 'LIVE_PENDING' ||
                campaignStatus === 'LIVE_APPROVED'
              ) {
                return null;
              }

              return (
                <Alert
                  message="Start Date Notice"
                  description="The campaign start date is in the past."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }
            return null;
          })()}

          <Row gutter={20}>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Budget</Text>
                <Text type="secondary">Campaign total spend</Text>
                <Form.Item
                  name="budget"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a budget',
                    },
                    () => ({
                      validator(_, value) {
                        if (value >= 1 || value === '') {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please input a valid budget'));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    addonBefore="$USD"
                    style={{ width: '100%' }}
                    min={0}
                    max={Math.pow(10, 12) - 1}
                    maxLength={12}
                    onChange={handleBudgetChange}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Start Date</Text>
                <Text type="secondary">Leave blank for ASAP</Text>
                <Form.Item
                  name="startDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a start date',
                    },
                  ]}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    onChange={handleStartDateChange}
                    disabledDate={(date) =>
                      !canEdit ? date.isBefore(moment.tz(timezone).startOf('day')) : false
                    }
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>End Date</Text>
                <Text type="secondary">Date campaign will end</Text>
                <Form.Item
                  name="endDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please input an end date',
                    },
                  ]}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    onChange={handleEndDateChange}
                    disabledDate={disabledEndDate}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>
    </>
  );
};

export default CampaignRequestorBudget;
